import { useState, useEffect, useMemo } from 'react'
import { loadIndex, getEnabledTopics, loadTopic } from '../data/topics/index.js'
import { getCurrentUser, checkPremiumStatus, onAuthChange, signOutUser, setPremiumStatus } from '../services/firebaseService'
import PaywallPopup from './PaywallPopup'
import '../styles/components/preference-selector.css'

function PreferenceSelectorV2({ onComplete, onError }) {
  const [topics, setTopics] = useState([])
  const [selectedTopics, setSelectedTopics] = useState([])
  const [topicItems, setTopicItems] = useState({}) // {topicId: {items: [], topic: {}}}
  const [selectedItems, setSelectedItems] = useState([]) // Array of {topicId, itemId}
  const [currentFilter, setCurrentFilter] = useState('all')
  const [expandedTopics, setExpandedTopics] = useState({}) // {topicId: true/false}
  const [loading, setLoading] = useState(true)
  const [showPaywall, setShowPaywall] = useState(false)
  const [paywallTopic, setPaywallTopic] = useState(null)
  const [user, setUser] = useState(null)
  const [hasPremium, setHasPremium] = useState(false)
  const [checkingPremium, setCheckingPremium] = useState(true)

  useEffect(() => {
    loadTopics()
    
    // Check if returning from successful payment
    const urlParams = new URLSearchParams(window.location.search)
    const isSuccess = urlParams.get('success') === 'true'
    const topicIdFromUrl = urlParams.get('topic_id') || sessionStorage.getItem('parkort_payment_topic_id')
    
    if (isSuccess) {
      // Clear URL params and sessionStorage after reading
      const newUrl = window.location.pathname
      window.history.replaceState({}, '', newUrl)
      sessionStorage.removeItem('parkort_payment_topic_id')
      
      // Re-check premium status when returning from payment - with multiple retries
      const checkPremiumAfterPayment = async (retryCount = 0) => {
        const currentUser = getCurrentUser()
        if (!currentUser) return
        
        const maxRetries = 5
        
        const premium = await checkPremiumStatus(currentUser.uid)
        setHasPremium(premium)
        
        // If still not premium, wait and check again (Firebase might need time)
        if (!premium && retryCount < maxRetries) {
          const delay = Math.min(1000 * (retryCount + 1), 3000) // 1s, 2s, 3s, 3s, 3s
          setTimeout(() => {
            checkPremiumAfterPayment(retryCount + 1)
          }, delay)
        }
      }
      
      checkPremiumAfterPayment()
      
      if (topicIdFromUrl) {
        // Wait for topics to load, then expand the topic
        setTimeout(() => {
          setExpandedTopics(prev => ({
            ...prev,
            [topicIdFromUrl]: true
          }))
          // Scroll to topic after a short delay
          setTimeout(() => {
            const topicElement = document.querySelector(`[data-topic-id="${topicIdFromUrl}"]`)
            if (topicElement) {
              topicElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          }, 300)
        }, 500)
      }
    }
    
    // Listen to auth state changes
    const unsubscribe = onAuthChange(async (currentUser) => {
      setUser(currentUser)
      if (currentUser) {
        setCheckingPremium(true)
        const premium = await checkPremiumStatus(currentUser.uid)
        setHasPremium(premium)
        setCheckingPremium(false)
      } else {
        setHasPremium(false)
        setCheckingPremium(false)
      }
    })

    return () => unsubscribe()
  }, [])

  async function loadTopics() {
    try {
      const enabledTopics = await getEnabledTopics()
      setTopics(enabledTopics)
      
      // Load items for each topic
      const itemsMap = {}
      for (const topic of enabledTopics) {
        try {
          const topicData = await loadTopic(topic.id)
          itemsMap[topic.id] = {
            items: topicData.items || [],
            topic: topicData
          }
        } catch (error) {
          console.error(`Error loading topic ${topic.id}:`, error)
          // Continue with other topics even if one fails
        }
      }
      setTopicItems(itemsMap)
      setLoading(false)
      
      // If no topics loaded, show error
      if (Object.keys(itemsMap).length === 0) {
        setLoading(false)
        // Show user-friendly error message
        if (onError) {
          onError()
        }
      }
    } catch (error) {
      setLoading(false)
      // Error loading topics - show user-friendly message
      if (onError) {
        onError()
      }
    }
  }

  const filteredTopics = useMemo(() => {
    if (currentFilter === 'all') {
      return topics
    }
    return topics.filter(topic => 
      topic.audiences.includes(currentFilter)
    )
  }, [topics, currentFilter])

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }))
  }

  const toggleItem = async (topicId, itemId) => {
    // Check if topic is paid
    const topic = topics.find(t => t.id === topicId)
    
    // DEV MODE: Allow all topics if VITE_DEV_MODE is enabled
    const devMode = import.meta.env.VITE_DEV_MODE === 'true' || localStorage.getItem('parkort_dev_mode') === 'true'
    
    if (topic && topic.tier === 'paid' && !devMode) {
      // Check if user has premium access via Firebase
      if (!user) {
        // User not logged in - show paywall (which will show auth modal)
        setPaywallTopic(topic)
        setShowPaywall(true)
        return
      }

      // Re-check premium status
      const premiumStatus = await checkPremiumStatus(user.uid)
      setHasPremium(premiumStatus)
      
      if (!premiumStatus) {
        // User logged in but no premium - show paywall
        setPaywallTopic(topic)
        setShowPaywall(true)
        return
      }
      // User has premium - allow selection
    }

    setSelectedItems(prev => {
      const key = `${topicId}-${itemId}`
      const exists = prev.find(s => s.topicId === topicId && s.itemId === itemId)
      
      if (exists) {
        return prev.filter(s => !(s.topicId === topicId && s.itemId === itemId))
      }
      return [...prev, { topicId, itemId }]
    })
  }

  const isItemSelected = (topicId, itemId) => {
    return selectedItems.some(s => s.topicId === topicId && s.itemId === itemId)
  }

  const handleProceed = () => {
    if (selectedItems.length === 0) {
      // Show friendly error instead of alert
      const guideBox = document.querySelector('.guide-box')
      if (guideBox) {
        guideBox.style.animation = 'shake 0.5s'
        setTimeout(() => {
          guideBox.style.animation = ''
        }, 500)
      }
      return
    }

    // Convert selected items to format expected by rest of app
    const preferences = selectedItems.map(sel => {
      const topicData = topicItems[sel.topicId]
      const item = topicData?.items.find(i => i.id === sel.itemId)
      return {
        id: sel.itemId,
        text: item?.text || '',
        topicId: sel.topicId,
        dimension: item?.dimension,
        responseType: item?.responseType || topicData?.topic?.defaults?.responseType || 'YNM'
      }
    })

    onComplete(preferences)
  }

  if (loading) {
    return (
      <div className="preference-selector step active" role="status" aria-live="polite">
        <div className="loading-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="loading-spinner" style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(102, 126, 234, 0.2)',
            borderTop: '4px solid var(--cyber-blue)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <h2>Indlæser moduler...</h2>
          <p style={{ marginTop: '20px', opacity: 0.7, color: 'var(--text-secondary)' }}>
            Henter emnerne klar til dig
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    )
  }

  if (topics.length === 0) {
    return (
      <div className="preference-selector step active">
        <div className="error-state" style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'rgba(255, 107, 107, 0.1)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 107, 107, 0.3)'
        }}>
          <div style={{ fontSize: '3em', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ marginBottom: '12px' }}>Ingen moduler fundet</h2>
          <p style={{ marginTop: '20px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
            Vi kunne ikke indlæse modulerne. Dette kan skyldes en midlertidig fejl.
          </p>
          <button className="btn" onClick={loadTopics} style={{ marginTop: '20px' }}>
            🔄 Prøv igen
          </button>
          <p style={{ marginTop: '16px', fontSize: '0.85em', opacity: 0.7 }}>
            Hvis problemet fortsætter, tjek browser console (F12) for fejl
          </p>
        </div>
      </div>
    )
  }

  // Function to manually refresh premium status
  const refreshPremiumStatus = async () => {
    const currentUser = getCurrentUser()
    if (currentUser) {
      setCheckingPremium(true)
      const premium = await checkPremiumStatus(currentUser.uid)
      setHasPremium(premium)
      setCheckingPremium(false)
      
      if (premium) {
        alert('✅ Premium status opdateret! Du har nu adgang til alle Premium moduler.')
      } else {
        alert('⚠️ Premium status ikke fundet. Hvis du lige har betalt, vent et øjeblik og prøv igen. Ellers kontakt support.')
      }
    } else {
      alert('Du skal være logget ind for at opdatere premium status.')
    }
  }

  // Function to manually set premium status (admin/debug)
  const manuallySetPremium = async () => {
    const currentUser = getCurrentUser()
    if (!currentUser) {
      alert('Du skal være logget ind.')
      return
    }

    const confirmed = confirm(`Vil du manuelt sætte premium status til TRUE for ${currentUser.email}?\n\nDette er en debug funktion.`)
    if (!confirmed) return

    try {
      const expiryDate = new Date()
      expiryDate.setFullYear(expiryDate.getFullYear() + 1) // 1 år frem
      
      await setPremiumStatus(currentUser.uid, true, expiryDate)
      const verified = await checkPremiumStatus(currentUser.uid)
      
      setHasPremium(verified)
      
      if (verified) {
        alert('✅ Premium status sat manuelt! Refresh siden for at se ændringen.')
        window.location.reload()
      } else {
        alert('⚠️ Kunne ikke verificere premium status. Tjek console.')
      }
    } catch (error) {
      console.error('Error setting premium:', error)
      alert('❌ Fejl: ' + error.message)
    }
  }

  // Function to logout
  const handleLogout = async () => {
    const confirmed = confirm('Er du sikker på at du vil logge ud?')
    if (!confirmed) return

    try {
      const result = await signOutUser()
      if (result.success) {
        setUser(null)
        setHasPremium(false)
        alert('✅ Logget ud! Refresh siden.')
        window.location.reload()
      } else {
        alert('❌ Fejl ved logout: ' + (result.error || 'Ukendt fejl'))
      }
    } catch (error) {
      console.error('Logout error:', error)
      alert('❌ Fejl ved logout: ' + error.message)
    }
  }

  return (
    <div className="preference-selector step active">
      <h2>Lav dit kort</h2>
      
      {/* User Info & Premium Status */}
      {user && (
        <div style={{
          marginBottom: '20px',
          padding: '16px',
          background: hasPremium ? 'rgba(81, 207, 102, 0.1)' : 'rgba(255, 193, 7, 0.1)',
          border: `1px solid ${hasPremium ? 'rgba(81, 207, 102, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
          borderRadius: '12px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <strong style={{ color: 'var(--text-primary)' }}>
                {hasPremium ? '✅ Premium Bruger' : '⚠️ Ikke Premium'}
              </strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                {user.email}
              </p>
            </div>
            <button 
              onClick={handleLogout}
              style={{
                background: 'rgba(255, 107, 107, 0.2)',
                color: 'var(--text-primary)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9em'
              }}
            >
              Log ud
            </button>
          </div>
          
          {!hasPremium && !checkingPremium && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button 
                className="btn" 
                onClick={refreshPremiumStatus}
                style={{
                  background: 'var(--cyber-blue)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9em'
                }}
              >
                🔄 Opdater Premium Status
              </button>
              <button 
                onClick={manuallySetPremium}
                style={{
                  background: 'rgba(102, 126, 234, 0.2)',
                  color: 'var(--text-primary)',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9em'
                }}
              >
                🔧 Sæt Premium Manuelt (Debug)
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Guide Box - Udvidet med Value Reminder */}
      <div className="guide-box" style={{
        background: 'rgba(102, 126, 234, 0.1)',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        fontSize: '0.95em',
        lineHeight: '1.6'
      }}>
        <div style={{ fontWeight: '700', marginBottom: '16px', color: 'var(--cyber-blue)', fontSize: '1.3em' }}>
          💡 Sådan laver du dit kort (3 enkle skridt)
        </div>
        <div style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
          <div style={{ 
            marginBottom: '12px', 
            padding: '12px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}>
            <strong style={{ fontSize: '1.1em' }}>1. Vælg emner:</strong> Vælg emner fra modulerne nedenfor, som du gerne vil have den andens ærlige mening om
          </div>
          <div style={{ 
            marginBottom: '12px', 
            padding: '12px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}>
            <strong style={{ fontSize: '1.1em' }}>2. ✏️ Svar selv først:</strong> Når du har valgt emner, skal du også svare på spørgsmålene først - dette giver bedre match og indsigt
          </div>
          <div style={{ 
            marginBottom: '12px', 
            padding: '12px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}>
            <strong style={{ fontSize: '1.1em' }}>3. Del med modtageren:</strong> Del linket med modtageren, som også svarer. I får begge match score og konkret feedback
          </div>
          <div style={{ 
            fontSize: '0.9em', 
            opacity: 0.8, 
            marginTop: '12px', 
            fontStyle: 'italic',
            padding: '12px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}>
            💬 Du kan vælge så mange eller få emner, som du har lyst til
          </div>
        </div>
        
        {/* Value Reminder */}
        {selectedItems.length > 0 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(81, 207, 102, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(81, 207, 102, 0.3)',
            fontSize: '0.9em'
          }}>
            ✅ Du har valgt {selectedItems.length} emner – perfekt til at starte samtalen!
            {selectedItems.length >= 3 && (
              <div style={{ marginTop: '4px', fontSize: '0.85em', opacity: 0.9 }}>
                Når modtageren svarer, får I begge en match score og konkret feedback.
              </div>
            )}
          </div>
        )}
        
        {/* Næste knap i guiden */}
        <button 
          className="btn" 
          onClick={handleProceed}
          disabled={selectedItems.length === 0}
          aria-label={selectedItems.length === 0 ? 'Vælg mindst ét emne for at fortsætte' : `Fortsæt med ${selectedItems.length} valgte emner`}
          aria-disabled={selectedItems.length === 0}
          style={{
            width: '100%',
            marginTop: '16px',
            opacity: selectedItems.length === 0 ? 0.5 : 1,
            cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {selectedItems.length === 0 
            ? '👆 Vælg mindst ét emne for at fortsætte' 
            : `✅ Næste (${selectedItems.length} valgt)`}
        </button>
      </div>


      {/* Filter Tabs - Vis alle filtre, også når valgt */}
      <div className="filter-tabs">
        <div 
          className={`filter-tab all ${currentFilter === 'all' ? 'active' : ''}`}
          onClick={() => setCurrentFilter('all')}
          role="button"
          tabIndex={0}
          aria-label="Vis alle moduler"
          aria-pressed={currentFilter === 'all'}
        >
          🌟 Vis alle
        </div>
        <div 
          className={`filter-tab ${currentFilter === 'par' ? 'active' : ''}`}
          onClick={() => setCurrentFilter('par')}
          role="button"
          tabIndex={0}
          aria-label="Filtrer moduler for par"
          aria-pressed={currentFilter === 'par'}
        >
          💕 Par {currentFilter === 'par' && '✓'}
        </div>
        <div 
          className={`filter-tab ${currentFilter === 'familie' ? 'active' : ''}`}
          onClick={() => setCurrentFilter('familie')}
          role="button"
          tabIndex={0}
          aria-label="Filtrer moduler for familie"
          aria-pressed={currentFilter === 'familie'}
        >
          👨‍👩‍👧‍👦 Familie {currentFilter === 'familie' && '✓'}
        </div>
        <div 
          className={`filter-tab ${currentFilter === 'kollegaer' ? 'active' : ''}`}
          onClick={() => setCurrentFilter('kollegaer')}
          role="button"
          tabIndex={0}
          aria-label="Filtrer moduler for kollegaer"
          aria-pressed={currentFilter === 'kollegaer'}
        >
          👥 Kollegaer {currentFilter === 'kollegaer' && '✓'}
        </div>
      </div>

      {/* Topics List - Collapsible */}
      <div className="preferences-list">
        {filteredTopics.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>Ingen topics fundet. Tjek browser console (F12) for errors.</p>
            <button className="btn" onClick={loadTopics} style={{ marginTop: '20px' }}>
              Prøv at loade igen
            </button>
          </div>
        ) : (
          filteredTopics.map(topic => {
            const topicData = topicItems[topic.id]
            const isExpanded = expandedTopics[topic.id]
            const miniAd = topicData?.topic?.positioning?.miniAd || topic.description || ''
            const itemCount = topicData?.items?.length || 0
            
            if (!topicData || !topicData.items.length) {
              return (
                <div key={topic.id} className="category-section" style={{ opacity: 0.5 }}>
                  <div className="category-title healing">
                    {topic.name} (Loading...)
                  </div>
                </div>
              )
            }

            return (
              <div key={topic.id} className="category-section topic-accordion" data-topic-id={topic.id}>
                <div 
                  className="category-title healing topic-header"
                  onClick={() => toggleTopic(topic.id)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    borderRadius: '8px',
                    background: isExpanded ? 'rgba(255,255,255,0.1)' : 'transparent',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{topic.name}</span>
                      {topic.tier === 'paid' && (
                        <span style={{
                          fontSize: '0.7em',
                          background: hasPremium ? 'rgba(81, 207, 102, 0.3)' : '#ff6b6b',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: 'bold'
                        }}>
                          {hasPremium ? '✨ PREMIUM' : '🔒 PREMIUM'}
                        </span>
                      )}
                      {topic.tier === 'free' && (
                        <span style={{
                          fontSize: '0.7em',
                          background: '#51cf66',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: 'bold'
                        }}>
                          GRATIS
                        </span>
                      )}
                    </div>
                    {miniAd && (
                      <div style={{ 
                        fontSize: '0.85em', 
                        marginTop: '8px', 
                        opacity: 0.9,
                        lineHeight: '1.4',
                        fontWeight: 'normal'
                      }}>
                        {miniAd}
                      </div>
                    )}
                    <div style={{ 
                      fontSize: '0.75em', 
                      marginTop: '6px', 
                      opacity: 0.7 
                    }}>
                      {itemCount} spørgsmål
                    </div>
                  </div>
                  <div style={{
                    fontSize: '1.5em',
                    transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ▼
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="category-preferences" style={{
                    padding: '12px',
                    animation: 'slideDown 0.3s ease-out'
                  }}>
                    {topicData.items.map((item) => {
                      const isPremiumItem = topic.tier === 'paid'
                      const isLocked = isPremiumItem && !hasPremium
                      
                      return (
                        <div
                          key={item.id}
                              className={`preference-toggle ${isItemSelected(topic.id, item.id) ? 'selected' : ''} ${isLocked ? 'premium-item' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleItem(topic.id, item.id)
                              }}
                              aria-label={isLocked ? `${item.text} - Klik for at opgradere til Premium` : item.text}
                              aria-pressed={isItemSelected(topic.id, item.id)}
                              style={{
                                opacity: isLocked ? 0.7 : 1,
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                minHeight: '44px', /* Touch target */
                                // Remove red glow when user has premium
                                boxShadow: isLocked ? undefined : 'none'
                              }}
                              title={isLocked ? 'Klik for at opgradere til Premium' : ''}
                              disabled={isLocked && !hasPremium}
                        >
                          {item.text}
                          {isLocked && (
                            <span style={{
                              marginLeft: '8px',
                              fontSize: '0.8em',
                              opacity: 0.7
                            }}>🔒</span>
                          )}
                          {isPremiumItem && hasPremium && (
                            <span style={{
                              marginLeft: '8px',
                              fontSize: '0.8em',
                              opacity: 0.7,
                              color: 'var(--cyber-blue)'
                            }}>✨</span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Selected Count */}
      {selectedItems.length > 0 && (
        <div className="selected-count">
          {selectedItems.length} valgt
        </div>
      )}

      {/* Proceed Button - også nederst for nem adgang */}
      <div style={{ 
        position: 'sticky', 
        bottom: '20px', 
        marginTop: '24px',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 10
      }}>
        <button 
          className="btn" 
          onClick={handleProceed}
          disabled={selectedItems.length === 0}
          aria-label={selectedItems.length === 0 ? 'Vælg emner først' : `Fortsæt med ${selectedItems.length} valgte emner`}
          style={{
            minWidth: '200px',
            minHeight: '48px', /* Touch target */
            fontSize: '1.1em',
            padding: '14px 28px',
            boxShadow: selectedItems.length > 0 ? '0 4px 12px rgba(102, 126, 234, 0.4)' : 'none',
            opacity: selectedItems.length === 0 ? 0.6 : 1,
            cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {selectedItems.length === 0 
            ? 'Vælg emner først' 
            : `✅ Næste (${selectedItems.length} valgt)`}
        </button>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 2000px;
          }
        }
        .topic-accordion {
          margin-bottom: 12px;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: '8px';
          overflow: hidden;
        }
        .topic-header:hover {
          background: rgba(255,255,255,0.05) !important;
        }
        @media (max-width: 768px) {
          .topic-header {
            padding: 12px !important;
          }
        }
      `}</style>

      {/* Paywall Popup */}
      <PaywallPopup 
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        topicName={paywallTopic?.name || 'Premium modul'}
        topicId={paywallTopic?.id}
      />
    </div>
  )
}

export default PreferenceSelectorV2
